/**
 * Page-scoped search for the biomarkers and diseases explorer surfaces.
 *
 * The global search modal/page uses the pre-built index + custom engine
 * in `engine.ts`. The two explorer pages (`/biomarkers`, `/what-we-detect`)
 * filter a live in-memory array of CMS items instead — they need to react
 * to the active package filter, the sort order, etc., which the static
 * index can't represent.
 *
 * ## Why a tiered match, not a single Fuse pass
 *
 * The first cut of this module wrapped Fuse in one config (threshold 0.3,
 * `ignoreLocation: true`, description as a third key). It fixed the typo
 * class the original `.includes()` filter missed — but on short queries
 * it produced wildly over-fuzzy results: `cort` matched 27 biomarkers,
 * `ster` matched 114 of 148 rows, `kortisol` matched "Volumen der
 * kortikalen grauen Substanz". Fuse can't gate an exact name hit above a
 * fuzzy description hit when both are in one pass — field weights re-rank
 * ties but don't tier.
 *
 * So the matcher is now explicitly tiered, the same shape Spotlight /
 * Linear / Algolia use. Each tier gates the next, and we return the
 * first tier with any hits:
 *
 *   1. Exact prefix on `name` or any alias
 *   2. Exact substring on `name` or any alias
 *   3. Fuzzy on `name` + aliases (Fuse, tightened) — gated to queries ≥ 4 chars
 *   4. Substring on `description` — gated to queries ≥ 5 chars
 *
 * Synonym expansion runs *inside* each tier: every locale variant
 * (`zink` → also try `zinc`) competes for tier-1 first, so a known
 * synonym wins before fuzzy gets a chance to misfire.
 *
 * ## Why the gating thresholds
 *
 * Short queries are almost always prefixes — `tsh`, `psa`, `ldl`, `dht`,
 * `b12`. Fuzzy on 3 chars is noise (Bitap edit budget is large relative to
 * query length); description matching on 3 chars trips on every other row.
 * The 4/5-char thresholds match the PostHog `search_query` data — every
 * over-match complaint from the v1 Fuse-only design was a ≤ 4-char query.
 *
 * Result order is the input order (alphabetical-by-name from the CMS),
 * not relevance — the explorer pages have a visual anchor (category
 * grouping), and reordering on each keystroke feels worse than keeping
 * rows in their categorized position.
 */
import Fuse, { type IFuseOptions } from "fuse.js"

import { contentTokens } from "./normalize"
import { SYNONYMS, type SynonymGroup } from "./synonyms"

export interface SearchableItem {
  /** Payload doc id — the locale-invariant identity. Carried so a searcher's
   *  `augmentAliases` can key the cross-locale alias map by id rather than the
   *  localized `slug` (which differs per locale). Optional: not every
   *  searchable item is a CMS doc. Postgres returns it as a number. */
  id?: string | number | null
  name: string
  description?: string | null
  // `value` is optional because Payload's locale fallback returns alias rows
  // keyed only by `id` (no `value`) in locales where the alias is unset — the
  // indexing pass below guards against that. Typing it as required `string`
  // is a lie that crashed /biomarkers in prod (Sentry 123128094).
  aliases?: { value?: string | null; id?: string | null }[] | null
}

/** Internal indexed shape — lowercased once at build time so every tier
 *  can compare without re-lowering on every keystroke × every row. */
interface IndexedItem<T extends SearchableItem> {
  item: T
  name: string
  aliases: string[]
  desc: string
}

/** Tighter than the original config: no `description` key (it's the noise
 *  pump — long fields × generous threshold = false positives), tighter
 *  threshold, location-anchored so matches must start near the field
 *  beginning, and a 3-char minimum match length so a 4-char query like
 *  `cort` can't pull in items by matching any 2-char fragment. */
const FUSE_OPTIONS: IFuseOptions<IndexedItem<SearchableItem>> = {
  keys: [
    { name: "name", weight: 4 },
    { name: "aliases", weight: 4 },
  ],
  threshold: 0.2,
  ignoreLocation: false,
  minMatchCharLength: 3,
  distance: 60,
}

/** Below this length, only exact tiers (1 + 2) run. Fuzzy matching on
 *  3-char queries is almost entirely noise — every reported false positive
 *  from the v1 design was a ≤ 4-char prefix. */
const FUZZY_MIN_QUERY_LENGTH = 4

/** Description-tier gate. A 4-char query can substring-match dozens of
 *  descriptions ("test", "iron", "blood"); 5+ is where description hits
 *  start to carry signal. */
const DESCRIPTION_MIN_QUERY_LENGTH = 5

/**
 * Build a memoizable searcher for a stable list of items. Callers should
 * memoize the return value (`useMemo([items, locale])`) so the lowercased
 * index + Fuse instance are built once per page mount.
 */
export function createPageSearcher<T extends SearchableItem>(
  items: readonly T[],
  locale: string,
  opts?: {
    /** Extra search terms per item, merged into its aliases. Used to feed the
     *  other-locale names of a biomarker/disease (from the generated
     *  cross-locale alias module) so a query in one language matches the
     *  catalog rendered in another — same reach the static index already has.
     */
    augmentAliases?: (item: T) => readonly string[]
  }
): {
  filter(query: string): T[]
} {
  const indexed: IndexedItem<T>[] = items.map((item) => ({
    item,
    name: item.name.toLowerCase(),
    // Payload returns localized array items keyed by `id` across locales,
    // so an alias that's only filled in one locale comes back as `{id}` (no
    // `value`) in the others — calling `.toLowerCase()` on that throws and
    // takes the whole page down (Sentry MARKETING-SITE: TypeError on
    // /ch/en/biomarkers). Match the safe pattern used by every other
    // consumer of `biomarker.aliases`.
    aliases: [
      ...(item.aliases ?? [])
        .map((a) => a?.value)
        .filter((v): v is string => typeof v === "string" && v.length > 0),
      ...(opts?.augmentAliases?.(item) ?? []),
    ].map((v) => v.toLowerCase()),
    desc: (item.description ?? "").toLowerCase(),
  }))

  const fuse = new Fuse(indexed, FUSE_OPTIONS as IFuseOptions<IndexedItem<T>>)
  const groups = SYNONYMS[locale] ?? SYNONYMS.en

  return {
    filter(query: string): T[] {
      const trimmed = query.trim().toLowerCase()
      if (!trimmed) return items.slice() as T[]

      const variants = expandQuery(trimmed, groups)

      // Tier 1: exact prefix on name or any alias.
      const t1 = pickMatches(indexed, (row) =>
        variants.some(
          (v) =>
            row.name.startsWith(v) || row.aliases.some((a) => a.startsWith(v))
        )
      )
      if (t1.size > 0) return preserveOrder(items, t1)

      // Tier 2: exact substring on name or any alias.
      const t2 = pickMatches(indexed, (row) =>
        variants.some(
          (v) => row.name.includes(v) || row.aliases.some((a) => a.includes(v))
        )
      )
      if (t2.size > 0) return preserveOrder(items, t2)

      // Tier 3: typo-tolerant fuzzy on name + aliases. Gated by length so
      // short prefixes don't trip Fuse's generous edit budget.
      if (trimmed.length >= FUZZY_MIN_QUERY_LENGTH) {
        const t3 = new Set<T>()
        for (const variant of variants) {
          for (const hit of fuse.search(variant)) t3.add(hit.item.item)
        }
        if (t3.size > 0) return preserveOrder(items, t3)
      }

      // Tier 4: description substring — discovers items whose body
      // mentions the term but whose name doesn't. Gated by length to
      // avoid matching every row on common short tokens.
      if (trimmed.length >= DESCRIPTION_MIN_QUERY_LENGTH) {
        const t4 = pickMatches(indexed, (row) =>
          variants.some((v) => row.desc.includes(v))
        )
        if (t4.size > 0) return preserveOrder(items, t4)
      }

      // Tier 5: per-token match, for multi-word queries only. Every tier above
      // matches the query as ONE string, so a query naming two things
      // ("shbg and albumin") or pairing a marker with a qualifier ("homa ir")
      // matched nothing at all — both are real 30d dead ends on /biomarkers
      // and /what-we-detect. Returning the markers the visitor named beats
      // returning an empty page.
      //
      // Last resort by construction: it only runs once tiers 1–4 are empty, so
      // it can never dilute a precise result set. Content tokens only (>=3
      // chars, no filler) so "and"/"der" can't blanket-match the catalog.
      // Gate on the RAW word count, not the content-token count: "homa ir"
      // leaves one content token once the 2-char "ir" is dropped, and that
      // case is exactly the dead end this tier exists for.
      const queryTokens = contentTokens(trimmed.split(" "))
      if (trimmed.split(" ").length > 1 && queryTokens.length > 0) {
        const t5 = pickMatches(indexed, (row) =>
          queryTokens.some(
            (t) =>
              row.name.includes(t) || row.aliases.some((a) => a.includes(t))
          )
        )
        if (t5.size > 0) return preserveOrder(items, t5)
      }

      return []
    },
  }
}

/** Filter the indexed rows by predicate, returning a Set of the original
 *  items. Set lets `preserveOrder` reorder back to input order in O(n). */
function pickMatches<T extends SearchableItem>(
  indexed: ReadonlyArray<IndexedItem<T>>,
  predicate: (row: IndexedItem<T>) => boolean
): Set<T> {
  const hits = new Set<T>()
  for (const row of indexed) {
    if (predicate(row)) hits.add(row.item)
  }
  return hits
}

/** Keep items in their original CMS-sorted (alphabetical) order. The
 *  explorer pages render category-grouped rows; ordering by match score
 *  would jumble rows past the visual anchor on each keystroke. */
function preserveOrder<T>(items: readonly T[], hits: Set<T>): T[] {
  return items.filter((item) => hits.has(item))
}

/**
 * Given a raw query like "zink supplement" in locale `de`, return every
 * synonym-swapped variant the dictionary suggests — here, `["zink supplement",
 * "zinc supplement"]` (since the cross-locale "zink"/"zinc" group hits the
 * first token).
 *
 * One swap at a time keeps the variant count linear in (tokens × matching
 * synonyms) instead of exponential, which is more than enough for the
 * single- and two-token queries this filter sees in practice.
 */
function expandQuery(
  query: string,
  groups: ReadonlyArray<SynonymGroup>
): string[] {
  const tokens = query.split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return [query]
  const variants = new Set<string>([query])
  for (let i = 0; i < tokens.length; i++) {
    for (const group of groups) {
      if (!group.includes(tokens[i])) continue
      for (const replacement of group) {
        if (replacement === tokens[i]) continue
        const swapped = tokens.slice()
        swapped[i] = replacement
        variants.add(swapped.join(" "))
      }
    }
  }
  return Array.from(variants)
}
